import React, { useState } from 'react';
import { Box, Button, Container, TextField, Typography, Alert, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material'; // Import visibility icons
import logo from '../images/applogomuhriz.png';
import { containerStyles, boxLogoStyles, boxFormStyles, textFieldStyles, buttonStyles } from './LoginPageStyles';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); 
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const db = getFirestore();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      setErrorMessage('Please enter both email and password.');
      return;
    }

    setLoading(true);

    try {
      const usersCollection = collection(db, 'employees');
      const userQuery = query(usersCollection, where('email', '==', email), where('password', '==', password));
      const querySnapshot = await getDocs(userQuery);

      if (querySnapshot.empty) {
        setErrorMessage('Invalid email or password.');
        return;
      }

      const userDoc = querySnapshot.docs[0].data();

      if (userDoc.role === 'admin') {
        navigate('/admin-dashboard', { state: { user: userDoc } });
      } else if (userDoc.role === 'employee') {
        navigate('/employee-dashboard', { state: { user: userDoc } });
      } else {
        setErrorMessage('User role is not recognized.');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setErrorMessage('An error occurred while trying to log in.');
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle password visibility
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth="none" sx={containerStyles}>
      {/* Left Side (Logo) */}
      <Box sx={boxLogoStyles}>
        <img src={logo} alt="Your Logo" style={{ width: '300px', height: '300px' }} />
      </Box>

      {/* Right Side (Login Form) */}
      <Box sx={boxFormStyles}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
          <Typography component="h6" variant="h6" sx={{ color: 'grey', paddingBottom: '10px' }}>
            Welcome to EMS
          </Typography>
          <Typography component="h5" variant="h5" sx={{ paddingBottom: '10px' }}>
            Sign in to your account
          </Typography>
        </div>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={textFieldStyles}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'} // Toggle between text and password
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textFieldStyles}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={buttonStyles}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
